import { toast } from 'react-toastify';
import { geocodingIndicator } from './geocodingIndicator';

const NOMINATIM_BASE = 'https://nominatim.openstreetmap.org';

async function fetchJson(url) {
  const res = await fetch(url, { headers: { 'Accept-Language': 'es' } });
  if (!res.ok) throw new Error(`Nominatim error: ${res.status}`);
  return res.json();
}

function geocodeAddress(direccion, localidad, barrio, partido) {
  const query = [direccion, barrio, localidad, partido].filter(Boolean).join(', ');
  if (!query) return Promise.resolve(null);
  const params = new URLSearchParams({ q: query, format: 'jsonv2', limit: '1', addressdetails: '1' });
  return fetchWithIndicator(async () => {
    const data = await fetchJson(`${NOMINATIM_BASE}/search?${params}`);
    if (!data || data.length === 0) return null;
    const first = data[0];
    // Evitar falsos positivos: si el nombre de calle buscado no coincide con el devuelto
    // por Nominatim (p. ej. "Juan Aguirre" -> "Juan José Castro"), no devolver coordenadas.
    if (!coincideNombreCalle(direccion, first.address?.road)) return null;
    const { lat, lon } = first;
    return { lat: parseFloat(lat), lng: parseFloat(lon), wkt: `POINT (${lon} ${lat})` };
  });
}

// Compara el nombre de calle buscado con el "road" que devuelve Nominatim para no
// aceptar coincidencias parciales/fuzzy de nombre y apellido (falsos positivos).
const PALABRAS_GENERICAS = new Set([
  'av', 'avenida', 'calle', 'c', 'pasaje', 'pje', 'camino', 'gral', 'general',
  'dr', 'doctor', 'de', 'del', 'la', 'las', 'los', 'el', 'san', 'santa',
  'col', 'coronel', 'y', 'e', 'entre', 'pas', 'paseo',
]);

function normalizar(texto) {
  return (texto || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function coincideNombreCalle(buscado, road) {
  if (!buscado || !road) return true;
  const tokensBuscado = normalizar(buscado)
    .split(' ')
    .filter((t) => t && !/^\d+$/.test(t) && !PALABRAS_GENERICAS.has(t));
  if (tokensBuscado.length === 0) return true;
  const tokensRoad = new Set(normalizar(road).split(' '));
  return tokensBuscado.every((t) => tokensRoad.has(t));
}

function reverseGeocodePoint(lat, lng) {
  const params = new URLSearchParams({
    format: 'jsonv2',
    lat: String(lat),
    lon: String(lng),
    addressdetails: '1',
  });
  return fetchWithIndicator(async () => {
    const data = await fetchJson(`${NOMINATIM_BASE}/reverse?${params}`);
    const addr = data.address || {};
    return {
      // Dirección corta: calle + altura (convención argentina). Si no hay road, fallback a display_name
      direccion: [addr.road, addr.house_number].filter(Boolean).join(' ') || data.display_name || '',
      localidad: addr.city || addr.town || addr.municipality || '',
      barrio: addr.neighbourhood || addr.suburb || addr.village || '',
    };
  });
}

// Envuelve la llamada a Nominatim con el indicador global "Obteniendo datos..." y
// muestra un Toast si la llamada falla (error de red o respuesta no-OK).
async function fetchWithIndicator(fn) {
  geocodingIndicator.start();
  try {
    return await fn();
  } catch {
    toast.error('Error de conexión con el servicio de geolocalización (OpenStreetMap).');
    return null;
  } finally {
    geocodingIndicator.end();
  }
}

export { geocodeAddress, reverseGeocodePoint };
